import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { TextButton } from '@flipster/react/design-system/text-button';
import Formatted from '@flipster/react/design-utils/formatted';
import TrashIcon from '@flipster/react/icon/generated/TrashIcon';
import { hstack } from '@flipster/styled-system/patterns';

import { useDeleteTpSl } from '../../trade-actions';
import { tradingSessionCheckBunja } from '../../trading-suspension/state';
import type { TpslButtonStateProps } from './identity';
import { tpslButtonBunja } from './state';
import { useTpslManageClick } from './use-tpsl-manage-click';

/**
 * TP/SL 단일(count 1) — trigger price 표시(편집 진입) + 삭제 아이콘.
 * 표시값·삭제 대상 주문을 tpslButtonBunja에서 직접 구독한다(position 객체를 받지 않는다).
 */
export const TpslButtonSingle = (props: TpslButtonStateProps) => {
  const { tpslType, accountId, size = 'small', disabled } = props;

  const { symbolAtom, tpFirstOrderAtom, slFirstOrderAtom, tpTriggerPriceAtom, slTriggerPriceAtom, pricePrecisionAtom } =
    useBunja(tpslButtonBunja);
  const symbol = useAtomValue(symbolAtom);
  const currentOrder = useAtomValue(tpslType === 'tp' ? tpFirstOrderAtom : slFirstOrderAtom);
  const triggerPrice = useAtomValue(tpslType === 'tp' ? tpTriggerPriceAtom : slTriggerPriceAtom);
  const precision = useAtomValue(pricePrecisionAtom);

  const { checkTradable } = useBunja(tradingSessionCheckBunja);
  const { deleteTpslQuery } = useDeleteTpSl();
  const { isPending: isLoading, mutate: deleteTpSl } = deleteTpslQuery;

  const handleEdit = useTpslManageClick({ ...props, guardTradable: true });

  const handleRemove = () => {
    if (!checkTradable() || !currentOrder) return;
    dialog.open({
      title: {
        label: (
          <FormattedMessage
            id={tpslType === 'tp' ? 'Position.r250401.Remove_tp_title' : 'Position.r250401.Remove_sl_title'}
          />
        ),
      },
      content: (
        <FormattedMessage
          id={tpslType === 'tp' ? 'Position.r250401.Remove_tp_description' : 'Position.r250401.Remove_sl_description'}
        />
      ),
      buttons: {
        primary: (
          <Dialog.Button
            hierarchy='primary'
            size='large'
            label={<FormattedMessage id='Common.r250401.Confirm' />}
            onClick={() => {
              deleteTpSl({ symbol, orderId: currentOrder.orderId, tpslType, accountId });
            }}
          />
        ),
        secondary: (
          <Dialog.Button
            hierarchy='secondary'
            color='neutral'
            size='large'
            label={<FormattedMessage id='Common.r250401.Cancel' />}
            onClick={() => {
              dialog.close();
            }}
          />
        ),
      },
    });
  };

  return (
    <div className={hstack({ gap: size === 'small' ? 4 : 8, alignItems: 'center' })}>
      <TextButton
        as='button'
        color='neutral'
        size={size}
        disabled={disabled}
        label={<Formatted.Number value={triggerPrice} precision={precision} toFixed />}
        onClick={handleEdit}
      />
      {currentOrder && (
        <IconButton
          as='button'
          hierarchy='quaternary'
          size='small'
          icon={<TrashIcon />}
          disabled={disabled || isLoading}
          onClick={handleRemove}
        />
      )}
    </div>
  );
};
